import {
  AccountStateKey,
  AccountStatus,
  AccountType,
} from '../account/account';
import { CountryCodeType } from '../common/countries';
import {
  BaseIdentifiers,
  TradingSystemIdentifier,
} from '../common/identifiers';
import { WithdrawRule } from '../common/withdraw-rule';
import { DashboardLayouts } from '../dashboard-layouts/dashboard-layouts';
import { NotificationName } from '../notification';
import { PartnerType } from '../partner';
import { Product } from '../product/product';
import { Ribbon } from '../ribbon/ribbon';
import { ConsistentRulesConfig, TradingSystem } from '../trading-system';
import { I18nEntry } from '../translation/translation';

export enum ProgramCategory {
  Evaluation = 'evaluation',
  Funded = 'funded',
  ScaleUp = 'scale-up',
}

export enum AssetClass {
  Forex = 'forex',
  Futures = 'futures',
  Stocks = 'stocks',
}

export interface ProgramAchievementConfig {
  enabled: boolean;
}

export type CreditEventData = { amount: number; expirationInDays: number };
export type CreditEvents = Record<AccountStateKey, CreditEventData>;

export interface ProgramTradingSystemConfig {
  groupId: string;
  terminationGroupId: string;
  tradingSystem: TradingSystem;
  tradingSystemFee?: number;
  sellable?: boolean;
}

export type CommissionsType = 'full-commission' | 'full-rebate';

export interface ProgramConfig {
  tradingSystems?: ProgramTradingSystemConfig[];
  excludedCountries?: CountryCodeType[];
  enableNews?: boolean;
  excludedCountriesMessage?: string;
  minTradeDuration?: number;
  purchaseAmount?: {
    limit: boolean;
    includeDisabledAccounts?: boolean;
    amount: number;
  };
  dashboardLayout?: DashboardLayouts;
  boosters?: Record<PartnerType, number>;
  target?: number;
  maxLoss?: number;
  programInitials?: string;
  leverage?: number;
  inactivityDays?: number;
  dailyPause?: number;
  dailyPausePercentage?: number;
  dailyLossPercentage?: number;
  buyingPower?: number;
  /**
   * Optional label to differentiate programs with identical buying power values.
   * Displayed as "Amount - Label" on frontend when present.
   * Example: "100K - Reversed"
   */
  buyingPowerLabel?: string;
  withdrawTarget?: number;
  consistencyRules?: ConsistentRulesConfig[];
  transferFunds?: boolean;
  transferFundsSubtractBaseBalance?: boolean;
  userPayoutPercentage?: number;
  terminationGroupId?: string;
  withdrawRules?: WithdrawRule[];
  targetEmail?: NotificationName;
  achievement?: ProgramAchievementConfig;
  ignoreProfitSplitOnTarget?: boolean;
  refundable?: number;
  creditEvents?: CreditEvents;
  programEligibility?: string;
  accountPreset?: number;
  maxLossTarget?: number;
  expiryDays?: number;
  discount?: {
    title: string;
    percentage: number;
    /**
     * Allow program discount to be applied when credits are used.
     * Default: false (credits override program discount)
     */
    allowWithCredits?: boolean;
    /**
     * Allow program discount to stack with affiliate discount.
     * Default: true (affiliate discount stacks with program discount)
     */
    allowWithAffiliateDiscount?: boolean;
    /**
     * Allow program discount to be applied when a coupon is used.
     * Default: false (coupons override program discount)
     */
    allowWithCoupon?: boolean;
  };
  /**
   * Block credits usage for this program.
   * When true, users cannot use credits to pay for this program.
   * Default: false (credits allowed)
   */
  blockCreditsUsage?: boolean;

  // Futures-specific fields
  /** End-of-Day trailing drawdown percentage */
  trailingEodPercentage?: number;
  /** Maximum number of Mini futures contracts allowed */
  maxContractSizeMini?: number;
  /** Maximum number of Micro futures contracts allowed */
  maxContractSizeMicro?: number;
  /** Commission structure for Futures programs */
  commissionsType?: CommissionsType;
  /** First payout cap percentage for Funded programs */
  firstPayoutCapPercentage?: number;
  /** Futures-specific scale up target */
  scaleUpTarget?: number;
  /** Whether accounts from this program are eligible for fee rebate */
  isFeeRebateEligible?: boolean;
}

export type Program = BaseIdentifiers &
  TradingSystemIdentifier &
  Product & {
    accountType: AccountType;
    accountStatus: AccountStatus;
    newUser?: boolean;
    groupId: string;
    expiryDays?: number;
    enabled: boolean;
    advised?: boolean;
    sellable: boolean;
    purchaseLimitation?: string;
    config: ProgramConfig;
    nextProgramId?: string;
    groupName?: string;
    programGroupId: string;
    ribbon?: Ribbon;
    quickBuyProgramId?: string;
    twinProgramId?: string;
    twinProgramLabel?: string;
    i18n?: I18nEntry[];
    assetClass: AssetClass;
  };

export interface ProgressStep {
  _id: string;
  name: string;
  buyingPower: number;
  target: number;

  // Numerical classification for precise positioning
  category: ProgramCategory;
  stepNumber: number; // 1, 2, 3, etc. within category
  totalInCategory: number; // Total steps in this category
  overallPosition: number; // 1-based position in entire progression
  totalPrograms: number; // Total programs in progression

  // Metadata for UI logic
  isFirstInCategory: boolean;
  isLastInCategory: boolean;
  isFinalProgram: boolean;

  // For progress calculation between nodes
  previousTarget?: number; // Target from previous step (for progress calc)
  cumulativeTarget: number; // Running total of targets to reach this step

  // Active state based on current user's program
  active: boolean;
}
